import React, { useState } from 'react';
import { DriverFormData } from '../../types/driver';

interface ReviewStepProps {
  data: DriverFormData;
  onSubmit: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ data, onSubmit }) => {
  const [confirmed, setConfirmed] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getFileStatus = (file: File | null, label?: string): string => {
    if (!file) return label ? `${label} not uploaded` : 'Not uploaded';
    return 'Uploaded';
  };

  const getFilesStatus = (files: File[]): string => {
    if (!files || files.length === 0) return 'Not uploaded';
    return `${files.length} file(s) uploaded`;
  };

  const Section = ({
    number,
    title,
    children,
  }: {
    number: number;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-2 shrink-0">
          {number}
        </span>
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  const Field = ({
    label,
    value,
    full,
    status,
  }: {
    label: string;
    value?: string;
    full?: boolean;
    status?: 'success' | 'error';
  }) => (
    <div className={full ? 'md:col-span-2' : ''}>
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <p
        className={
          status === 'success'
            ? 'font-medium text-green-600'
            : status === 'error'
            ? 'font-medium text-red-600'
            : 'text-gray-900'
        }
      >
        {value || 'Not provided'}
      </p>
    </div>
  );

  const handleSubmit = () => {
    if (!confirmed) {
      alert('Please confirm that all information is accurate before submitting.');
      return;
    }
    onSubmit();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Information</h2>
      <p className="text-gray-600 mb-6">
        Please review all your information carefully before submitting.
      </p>

      <div className="space-y-6">
        <Section number={1} title="Personal Information">
          <Field label="Full Name" value={data.fullName} />
          <Field label="Date of Birth" value={formatDate(data.dateOfBirth)} />
          <Field label="Email Address" value={data.email} />
          <Field label="NIN" value={data.nin} />
          <Field label="National ID / Passport" value={data.nationalId} />
          <Field label="State of Origin" value={data.stateOfOrigin} />
          <Field label="Residential Address" value={data.address} full />
        </Section>

        <Section number={2} title="Next of Kin">
          <Field label="Full Name" value={data.nextOfKinName} />
          <Field label="Phone Number" value={data.nextOfKinPhone} />
          <Field label="Relationship" value={data.nextOfKinRelationship} />
        </Section>

        <Section number={3} title="Driver's License">
          <Field label="License Number" value={data.licenseNumber} />
          <Field label="Expiry Date" value={formatDate(data.licenseExpiry)} />
          <Field label="Driving Experience" value={data.drivingExperience} />
          <Field label="License Front" value={getFileStatus(data.licenseFront)} />
          <Field label="License Back" value={getFileStatus(data.licenseBack)} />
        </Section>

        <Section number={4} title="Vehicle Information">
          <Field label="Registration Number" value={data.vehicleRegistration} />
          <Field label="Make" value={data.vehicleMake} />
          <Field label="Model" value={data.vehicleModel} />
          <Field label="Color" value={data.vehicleColor} />
          <Field label="Year" value={data.vehicleYear} />
          <Field label="Vehicle Photos" value={getFilesStatus(data.vehiclePhotos)} />
        </Section>

        <Section number={5} title="Banking Information">
          <Field label="Bank Name" value={data.bankName} />
          <Field label="Account Number" value={data.accountNumber} />
          <Field label="Account Name" value={data.accountName} full />
        </Section>

        <Section number={6} title="Documents & Background Checks">
          <Field
            label="Criminal Record Check"
            value={data.criminalRecordCheck ? 'Declared' : 'Not declared'}
            status={data.criminalRecordCheck ? 'success' : 'error'}
          />
          <Field
            label="Driving Record Check"
            value={data.drivingRecordCheck ? 'Declared' : 'Not declared'}
            status={data.drivingRecordCheck ? 'success' : 'error'}
          />
          <Field label="Profile Photo" value={getFileStatus(data.profilePhoto)} />
          <Field label="Passport Photograph" value={getFileStatus(data.passportPhoto)} />
          <Field
            label="Insurance Certificate"
            value={data.insuranceCertificate ? 'Uploaded' : 'Not provided (optional)'}
          />
          <Field
            label="Roadworthiness Certificate"
            value={data.roadworthinessCertificate ? 'Uploaded' : 'Not provided (optional)'}
          />
        </Section>

        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Final Confirmation</h3>
          <div className="flex items-start">
            <input
              id="finalConfirmation"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded mt-0.5"
            />
            <div className="ml-3 text-sm">
              <label htmlFor="finalConfirmation" className="font-medium text-blue-800">
                I confirm that all information provided is accurate and complete
              </label>
              <p className="text-blue-700 mt-1">
                I understand that providing false information may result in immediate termination
                of my driver account and legal consequences. I authorize Xryde to verify all
                submitted information and documents.
              </p>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!confirmed}
            className={`mt-6 w-full py-3 rounded-md font-medium transition-colors duration-200 ${
              confirmed
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Submit Verification
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;